import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { registerUser, selectAuthUser } from '../store/authSlice.js';
import {
  resetForm,
  resetMessages,
  selectRegisterUserForm,
  sendWelcomeEmail,
  setError,
  setSuccess,
  updateFormField,
} from '../store/registerUserFormSlice.js';

// This is the full list of all possible roles in the system.
const allUserRoles = [
  'Super Admin',
  'Admin',
  'Verifier',
  'Collector',
  'Payment Redeemer',
  'Payment Poster',
  'Hearing Representative',
  'Provider'
];

const RegisterUserForm = () => {
  const dispatch = useDispatch();
  const { formData, error, success, mailStatus } = useSelector(selectRegisterUserForm);
  const user = useSelector(selectAuthUser);

  // Filter the roles that can be assigned based on the current user's role.
  const availableRoles = useMemo(() => {
    if (user && user.role === 'Admin') {
      return allUserRoles.filter(role => role !== 'Super Admin');
    }
    return allUserRoles;
  }, [user]);

  const handleChange = (e) => {
    dispatch(updateFormField({ field: e.target.name, value: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(resetMessages());

    // Keep a copy before we reset the form
    const payload = { ...formData };

    const result = await dispatch(registerUser(payload)).unwrap();
    if (result?.success) {
      dispatch(setSuccess('User registered successfully!'));
      // Fire-and-forget welcome email (reports its own status in UI)
      dispatch(sendWelcomeEmail({
        email: payload.email,
        username: payload.username,
        password: payload.password,
        fullName: payload.fullName,
      }));

      // Reset form
      dispatch(resetForm());
    } else {
      dispatch(setError(result?.message || 'Failed to register user.'));
    }
  };

  return (
    <div className="p-8 bg-white rounded-lg  w-full max-w-2xl mx-auto mt-8">
      <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">Create a New User Account</h2>
      <form onSubmit={handleSubmit}>
        {error && (
          <p className="text-red-700 bg-red-100 border border-red-200 p-3 rounded-md mb-4">
            {error}
          </p>
        )}
        {success && (
          <p className="text-green-800 bg-green-100 border border-green-200 p-3 rounded-md mb-4">
            {success}
          </p>
        )}
        {!!mailStatus.message && (
          <p className={`p-3 rounded-md mb-4 border ${
            mailStatus.message.includes('failed')
              ? 'text-yellow-900 bg-yellow-100 border-yellow-200'
              : 'text-blue-900 bg-blue-100 border-blue-200'
          }`}>
            {mailStatus.sending ? 'Sending welcome email…' : mailStatus.message}
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fullName">Full Name</label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              id="fullName"
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="username">Username</label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              id="username"
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">Email</label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">Password</label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              id="password"
              type="password"
              name="password"
              value={formData.password}
              minLength="6"
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-6 md:col-span-2">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="role">Role</label>
            <select
              name="role"
              id="role"
              onChange={handleChange}
              value={formData.role}
              className="shadow border rounded w-full py-2 px-3 text-gray-700"
            >
              {availableRoles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-center mt-4">
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded focus:outline-none focus:shadow-outline disabled:opacity-60"
            type="submit"
            disabled={mailStatus.sending}
          >
            {mailStatus.sending ? 'Registering…' : 'Register User'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegisterUserForm;
